import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Container,
  AppBar,
  Toolbar,
  IconButton,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Avatar,
  Chip,
  Grid,
  Alert,
  LinearProgress
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  EmojiEvents as TrophyIcon,
  Home as HomeIcon,
  Share as ShareIcon,
  Refresh as RefreshIcon,
  CheckCircle as CheckIcon,
  Cancel as CancelIcon,
  Speed as SpeedIcon,
  Timer as TimerIcon,
  MusicNote as MusicIcon
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { useSnackbar } from 'notistack';

// Styled components
const ResultsContainer = styled(Box)(({ theme }) => ({
  backgroundColor: '#121212',
  minHeight: '100vh',
  padding: '24px',
}));

const StyledCard = styled(Card)(({ theme }) => ({
  backgroundColor: '#1A1A1A',
  border: '1px solid #333',
  borderRadius: '16px',
  marginBottom: '24px',
  transition: 'all 0.3s ease',
}));

const TrophyCard = styled(Card)(({ theme, position }) => ({
  backgroundColor: position === 1 ? '#FFD700' : 
                   position === 2 ? '#C0C0C0' : 
                   position === 3 ? '#CD7F32' : '#1A1A1A',
  border: position <= 3 ? '2px solid' : '1px solid #333',
  borderColor: position === 1 ? '#FFD700' : 
               position === 2 ? '#C0C0C0' : 
               position === 3 ? '#CD7F32' : '#333',
  borderRadius: '16px',
  marginBottom: '24px',
  transition: 'all 0.3s ease',
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: position <= 3 ? '0 12px 32px rgba(255, 215, 0, 0.3)' : '0 8px 24px rgba(0, 0, 0, 0.3)',
  },
}));

const ActionButton = styled(Button)(({ theme, variant }) => ({
  borderRadius: '12px',
  textTransform: 'none',
  fontWeight: 600,
  padding: '12px 24px',
  fontSize: '1rem',
  ...(variant === 'primary' && {
    backgroundColor: '#FF9800',
    color: '#fff',
    '&:hover': {
      backgroundColor: '#F57C00',
      transform: 'translateY(-2px)',
      boxShadow: '0 8px 24px rgba(255, 152, 0, 0.3)',
    },
  }),
  ...(variant === 'secondary' && {
    backgroundColor: '#4CAF50',
    color: '#fff',
    '&:hover': {
      backgroundColor: '#45a049',
      transform: 'translateY(-2px)',
      boxShadow: '0 8px 24px rgba(76, 175, 80, 0.3)',
    },
  }),
}));

const RapidQuizResults = ({ 
  session, 
  participant, 
  userAnswers = [], 
  finalScore = 0, 
  finalPosition = 0,
  leaderboard = [],
  onBack,
  onPlayAgain,
  onGoHome 
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const [stats, setStats] = useState({
    totalQuestions: 0,
    correctAnswers: 0,
    incorrectAnswers: 0,
    averageTime: 0,
    totalTime: 0
  });

  useEffect(() => {
    // Calculate stats from user answers
    const totalQuestions = userAnswers.length;
    const correctAnswers = userAnswers.filter(answer => answer.is_correct).length;
    const incorrectAnswers = totalQuestions - correctAnswers;
    const totalTime = userAnswers.reduce((sum, answer) => sum + (answer.time_taken || 0), 0);
    const averageTime = totalQuestions > 0 ? totalTime / totalQuestions : 0;

    setStats({
      totalQuestions,
      correctAnswers,
      incorrectAnswers,
      averageTime: Math.round(averageTime * 10) / 10,
      totalTime: Math.round(totalTime)
    });
  }, [userAnswers]);

  const getPositionColor = (position) => {
    switch (position) {
      case 1: return '#FFD700';
      case 2: return '#C0C0C0';
      case 3: return '#CD7F32';
      default: return '#666';
    }
  };

  const getPositionIcon = (position) => {
    switch (position) {
      case 1: return '🥇';
      case 2: return '🥈';
      case 3: return '🥉';
      default: return '🏆';
    }
  };

  const getPerformanceMessage = () => {
    const accuracy = stats.totalQuestions > 0 ? (stats.correctAnswers / stats.totalQuestions) * 100 : 0;
    
    if (accuracy >= 90) return { message: 'Outstanding!', color: '#4CAF50' };
    if (accuracy >= 80) return { message: 'Excellent!', color: '#4CAF50' };
    if (accuracy >= 70) return { message: 'Great job!', color: '#FF9800' };
    if (accuracy >= 60) return { message: 'Good effort!', color: '#FF9800' };
    return { message: 'Keep practicing!', color: '#F44336' };
  };

  const performance = getPerformanceMessage();

  return (
    <ResultsContainer>
      <AppBar position="static" sx={{ bgcolor: 'background.paper', boxShadow: 1, mb: 3 }}>
        <Toolbar>
          <IconButton onClick={onBack} sx={{ mr: 2 }}>
            <BackIcon />
          </IconButton>
          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
            Quiz Results - {session?.quiz_name}
          </Typography>
          <IconButton onClick={() => window.location.reload()}>
            <RefreshIcon />
          </IconButton>
        </Toolbar>
      </AppBar>

      <Container maxWidth="lg">
        {/* Header */}
        <Box textAlign="center" mb={4}>
          <TrophyIcon sx={{ fontSize: 80, color: getPositionColor(finalPosition), mb: 2 }} />
          <Typography variant="h3" sx={{ color: '#fff', fontWeight: 700, mb: 2 }}>
            Quiz Complete!
          </Typography>
          <Typography variant="h6" sx={{ color: '#ccc', fontWeight: 400 }}>
            {performance.message}
          </Typography>
        </Box>

        {/* Final Position Card */}
        <TrophyCard position={finalPosition} sx={{ mb: 4 }}>
          <CardContent sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant="h2" sx={{ color: '#fff', fontWeight: 700, mb: 2 }}>
              {getPositionIcon(finalPosition)} #{finalPosition}
            </Typography>
            <Typography variant="h4" sx={{ color: '#fff', fontWeight: 600, mb: 2 }}>
              Final Position
            </Typography>
            <Typography variant="h3" sx={{ color: getPositionColor(finalPosition), fontWeight: 700, mb: 2 }}>
              {finalScore} Points
            </Typography>
            <Typography variant="body1" sx={{ color: '#B0B0B0' }}>
              Out of {leaderboard.length} players
            </Typography>
          </CardContent>
        </TrophyCard>

        {/* Stats Cards */}
        <Grid container spacing={3} mb={4}>
          <Grid item xs={12} sm={6} md={3}>
            <StyledCard>
              <CardContent sx={{ textAlign: 'center' }}>
                <CheckIcon sx={{ fontSize: 40, color: '#4CAF50', mb: 1 }} />
                <Typography variant="h4" sx={{ color: '#fff', fontWeight: 700 }}>
                  {stats.correctAnswers}
                </Typography>
                <Typography variant="body2" sx={{ color: '#ccc' }}>
                  Correct Answers
                </Typography>
              </CardContent>
            </StyledCard>
          </Grid>
          
          <Grid item xs={12} sm={6} md={3}>
            <StyledCard>
              <CardContent sx={{ textAlign: 'center' }}>
                <CancelIcon sx={{ fontSize: 40, color: '#F44336', mb: 1 }} />
                <Typography variant="h4" sx={{ color: '#fff', fontWeight: 700 }}>
                  {stats.incorrectAnswers}
                </Typography>
                <Typography variant="body2" sx={{ color: '#ccc' }}>
                  Incorrect Answers
                </Typography>
              </CardContent>
            </StyledCard>
          </Grid>
          
          <Grid item xs={12} sm={6} md={3}>
            <StyledCard>
              <CardContent sx={{ textAlign: 'center' }}>
                <SpeedIcon sx={{ fontSize: 40, color: '#FF9800', mb: 1 }} />
                <Typography variant="h4" sx={{ color: '#fff', fontWeight: 700 }}>
                  {stats.averageTime}s
                </Typography>
                <Typography variant="body2" sx={{ color: '#ccc' }}>
                  Avg. Response Time
                </Typography>
              </CardContent>
            </StyledCard>
          </Grid>
          
          <Grid item xs={12} sm={6} md={3}>
            <StyledCard>
              <CardContent sx={{ textAlign: 'center' }}>
                <TimerIcon sx={{ fontSize: 40, color: '#2196F3', mb: 1 }} />
                <Typography variant="h4" sx={{ color: '#fff', fontWeight: 700 }}>
                  {stats.totalTime}s
                </Typography>
                <Typography variant="body2" sx={{ color: '#ccc' }}>
                  Total Time
                </Typography>
              </CardContent>
            </StyledCard>
          </Grid>
        </Grid>

        {/* Accuracy Progress */}
        <StyledCard sx={{ mb: 4 }}>
          <CardContent>
            <Typography variant="h6" sx={{ color: '#fff', mb: 2, fontWeight: 600 }}>
              Accuracy Breakdown
            </Typography>
            <Box sx={{ mb: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body1" sx={{ color: '#fff' }}>
                  Correct Answers
                </Typography>
                <Typography variant="body1" sx={{ color: '#4CAF50', fontWeight: 600 }}>
                  {stats.correctAnswers}/{stats.totalQuestions} ({Math.round((stats.correctAnswers / stats.totalQuestions) * 100)}%)
                </Typography>
              </Box>
              <LinearProgress 
                variant="determinate" 
                value={(stats.correctAnswers / stats.totalQuestions) * 100}
                sx={{
                  height: 8,
                  borderRadius: 4,
                  backgroundColor: '#333',
                  '& .MuiLinearProgress-bar': {
                    backgroundColor: '#4CAF50',
                    borderRadius: 4,
                  }
                }}
              />
            </Box>
          </CardContent>
        </StyledCard>

        {/* Final Leaderboard */}
        <StyledCard sx={{ mb: 4 }}>
          <CardContent>
            <Typography variant="h5" sx={{ color: '#fff', mb: 3, fontWeight: 600, display: 'flex', alignItems: 'center' }}>
              <TrophyIcon sx={{ mr: 1, color: '#FFD700' }} />
              Final Leaderboard
            </Typography>

            {leaderboard.length > 0 ? (
              <List>
                {leaderboard.slice(0, 10).map((entry, index) => {
                  const position = index + 1;
                  const isCurrentUser = entry.participant_id === participant?.participant_id;
                  
                  return (
                    <ListItem 
                      key={entry.participant_id} 
                      sx={{ 
                        py: 2,
                        backgroundColor: isCurrentUser ? 'rgba(255, 152, 0, 0.1)' : 'transparent',
                        borderRadius: '8px',
                        mb: 1,
                        border: isCurrentUser ? '1px solid #FF9800' : 'none'
                      }}
                    >
                      <ListItemAvatar>
                        <Avatar sx={{ 
                          bgcolor: getPositionColor(position),
                          width: 40,
                          height: 40,
                          fontSize: '1rem',
                          fontWeight: 'bold'
                        }}>
                          {position}
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary={
                          <Typography variant="h6" sx={{ color: '#fff', fontWeight: 600 }}>
                            {(entry.fname && entry.lname) ? `${entry.fname} ${entry.lname}` : 
                             entry.guest_name || 
                             entry.anonymous_id || 
                             'Anonymous'}
                            {isCurrentUser && (
                              <Chip 
                                label="You" 
                                size="small" 
                                sx={{ 
                                  ml: 1, 
                                  backgroundColor: '#FF9800', 
                                  color: '#fff',
                                  fontSize: '0.7rem'
                                }} 
                              />
                            )}
                          </Typography>
                        }
                        secondary={
                          <Typography variant="body1" sx={{ color: '#B0B0B0' }}>
                            {entry.current_score || 0} points
                          </Typography>
                        }
                      />
                    </ListItem>
                  );
                })}
                {leaderboard.length > 10 && (
                  <ListItem sx={{ py: 2 }}>
                    <Typography variant="body2" sx={{ color: '#666', textAlign: 'center', width: '100%' }}>
                      ... and {leaderboard.length - 10} more players
                    </Typography>
                  </ListItem>
                )}
              </List>
            ) : (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <Typography variant="body1" sx={{ color: '#666' }}>
                  No leaderboard data available
                </Typography>
              </Box>
            )}
          </CardContent>
        </StyledCard>

        {/* Thank You Message */}
        <StyledCard sx={{ mb: 4 }}>
          <CardContent sx={{ textAlign: 'center', py: 4 }}>
            <MusicIcon sx={{ fontSize: 60, color: '#FF9800', mb: 2 }} />
            <Typography variant="h5" sx={{ color: '#fff', fontWeight: 600, mb: 2 }}>
              Thank You for Playing!
            </Typography>
            <Typography variant="body1" sx={{ color: '#B0B0B0', mb: 3 }}>
              We hope you enjoyed the rapid quiz experience. Keep practicing to improve your music knowledge!
            </Typography>
            <Alert severity="success" sx={{ mb: 3 }}>
              <Typography variant="body2">
                Your results have been saved and you can view them anytime in your quiz history.
              </Typography>
            </Alert>
          </CardContent>
        </StyledCard>

        {/* Action Buttons */}
        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
          <ActionButton
            variant="primary"
            size="large"
            startIcon={<HomeIcon />}
            onClick={onGoHome}
            sx={{ minWidth: '200px' }}
          >
            Go Home
          </ActionButton>
          
          <ActionButton
            variant="secondary"
            size="large"
            startIcon={<RefreshIcon />}
            onClick={onPlayAgain}
            sx={{ minWidth: '200px' }}
          >
            Play Again
          </ActionButton>
          
          <Button
            variant="outlined"
            size="large"
            startIcon={<ShareIcon />}
            sx={{
              minWidth: '200px',
              borderColor: '#333',
              color: '#ccc',
              '&:hover': {
                borderColor: '#555',
                backgroundColor: '#222'
              }
            }}
            onClick={() => {
              navigator.clipboard.writeText(`I scored ${finalScore} points and ranked #${finalPosition} in the rapid quiz! 🎵`);
              enqueueSnackbar('Results copied to clipboard!', { variant: 'success' });
            }}
          >
            Share Results
          </Button>
        </Box>

        {/* Footer */}
        <Box textAlign="center" mt={4}>
          <Typography variant="body2" sx={{ color: '#666' }}>
            Thanks for playing Rapid Quiz! 🎵🏆
          </Typography>
        </Box>
      </Container>
    </ResultsContainer>
  );
};

export default RapidQuizResults;

